import { useState, useCallback, useEffect, useMemo } from 'react';
import { message, Modal } from 'antd';
import {
  UserAddOutlined, DeleteOutlined, EditOutlined, SearchOutlined, ReloadOutlined,
  TeamOutlined, CrownOutlined, ClockCircleOutlined, StopOutlined, MailOutlined,
} from '@ant-design/icons';

import {
  genericGETDatabaseCall, genericPOSTDatabaseCall, genericDELETEDatabaseCall,
} from '../helperFunctions.jsx';
import { PageHead, StatTile } from '../FinancialBuilder/fbUI.jsx';
import Unauthorized from '../Unauthorized/Unauthorized.jsx';
import '../FinancialBuilder/financial-builder.css';
import './Admin.css';

/* ============================ Server calls ============================
   The backend owns authorization: /verify decides whether the caller is an
   admin (this page only draws the result, it never decides access itself),
   and /addUser generates the password and emails it to the new user. */

async function fetchAdminStatusFromServer() {
  return genericGETDatabaseCall('/api/admin/verify', 'admin status'); // done
}

async function fetchUsersFromServer() {
  return genericGETDatabaseCall('/api/admin/getUsers', 'users'); // done
}

async function addUserOnServer(user) {
  return genericPOSTDatabaseCall('/api/admin/addUser', user, 'user'); // done
}

async function updateUserOnServer(user) {
  return genericPOSTDatabaseCall('/api/admin/updateUser', user, 'user'); // done
}

async function deleteUserOnServer(userId) {
  return genericDELETEDatabaseCall('/api/admin/deleteUser', { userId }, 'user'); //done
}

const SUBSCRIPTION_TIERS = ['free', 'premium'];
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* ============================ Date helpers ============================ */

/* TIMESTAMPTZ -> the YYYY-MM-DD that <input type="date"> requires, in the
   admin's local time so the date they see is the date they picked. */
function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/* Expiry lands at the end of the chosen day, so a plan set to expire "today"
   stays valid for the rest of it — checkAuth compares straight against NOW(). */
function fromDateInput(value) {
  if (!value) return null;
  const d = new Date(`${value}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function addMonths(date, months) {
  const d = new Date(date);
  const targetDay = d.getDate();
  d.setMonth(d.getMonth() + months);
  // Clamp Jan 31 + 1mo to Feb 28/29 rather than letting it roll into March.
  if (d.getDate() < targetDay) d.setDate(0);
  return d;
}

/* Renewals extend from whichever is later: today, or the existing expiry — so
   renewing early tops up the remaining time instead of throwing it away. */
function extendFrom(currentExpiry) {
  const now = new Date();
  if (!currentExpiry) return now;
  const d = new Date(currentExpiry);
  return Number.isNaN(d.getTime()) || d < now ? now : d;
}

function daysUntil(value) {
  return Math.ceil((new Date(value) - new Date()) / (1000 * 60 * 60 * 24));
}

/* The one thing an admin actually scans this table for. Mirrors the backend's
   getSubscriptionStatusReport: a null expiry is a hard lockout (402), not a
   blank field, so it reads as an error rather than as missing data. */
function expiryStatus(user) {
  if (!user.subscription_expires_at) {
    return { tone: 'none', label: 'No access', hint: 'No expiry set — this user is locked out of the app.' };
  }
  const days = daysUntil(user.subscription_expires_at);
  if (days <= 0) return { tone: 'expired', label: 'Expired', hint: `Expired ${Math.abs(days)} day(s) ago.` };
  if (days <= 7) return { tone: 'soon', label: `${days}d left`, hint: `Expires in ${days} day(s).` };
  return { tone: 'active', label: 'Active', hint: `Expires in ${days} day(s).` };
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toDateString();
}

/* ============================ Shared bits ============================ */

function Field({ label, hint, children, span }) {
  return (
    <div className={'pn-field' + (span ? ' span2' : '')}>
      <label>{label}</label>
      {children}
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

function ExpiryPresets({ currentExpiry, onPick }) {
  const presets = [
    { label: '+1 month', months: 1 },
    { label: '+6 months', months: 6 },
    { label: '+1 year', months: 12 },
  ];
  return (
    <div className="pn-admin-presets">
      {presets.map((p) => (
        <button key={p.label} type="button" className="pn-btn pn-btn-ghost pn-btn-sm"
          onClick={() => onPick(toDateInput(addMonths(extendFrom(currentExpiry), p.months)))}>
          {p.label}
        </button>
      ))}
      <button type="button" className="pn-btn pn-btn-ghost pn-btn-sm" onClick={() => onPick('')}>
        Clear
      </button>
    </div>
  );
}

/* ============================ Add account ============================ */

const EMPTY_DRAFT = { name: '', email: '', subscription: 'free', expiresOn: '' };

function AddUserModal({ open, onClose, onCreated }) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      // Default a new account to a year of access — an account created with no
      // expiry can't sign in past the subscription check.
      setDraft({ ...EMPTY_DRAFT, expiresOn: toDateInput(addMonths(new Date(), 12)) });
      setError('');
    }
  }, [open]);

  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  const submit = async () => {
    const name = draft.name.trim();
    const email = draft.email.trim();
    if (!name) return setError('Name is required.');
    if (!emailRegex.test(email)) return setError('Enter a valid email address.');
    if (!draft.expiresOn) return setError('Pick an expiry date — without one the account cannot access the app.');

    setSaving(true);
    setError('');
    try {
      await onCreated({
        name,
        email,
        subscription: draft.subscription,
        subscription_expires_at: fromDateInput(draft.expiresOn),
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create the account.');
    }
    setSaving(false);
  };

  return (
    <Modal title="Add account" open={open} onCancel={onClose} destroyOnClose footer={null} width={520}>
      <div className="pn-admin-form">
        <div className="pn-admin-note">
          <MailOutlined className="ic" />
          <span>The server generates a password and emails it to the user — you don't set one here.</span>
        </div>

        <div className="pn-fields pn-fields-1">
          <Field label="Full name">
            <input className="pn-control" value={draft.name} onChange={set('name')}
              placeholder="Jane Doe" autoFocus />
          </Field>
          <Field label="Email" hint="Their sign-in address, and where the password is sent.">
            <input className="pn-control" type="email" value={draft.email} onChange={set('email')}
              placeholder="jane@company.com" />
          </Field>
        </div>

        <div className="pn-fields">
          <Field label="Plan">
            <select className="pn-control" value={draft.subscription} onChange={set('subscription')}>
              {SUBSCRIPTION_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Access expires">
            <input className="pn-control" type="date" value={draft.expiresOn} onChange={set('expiresOn')} />
          </Field>
        </div>

        <ExpiryPresets currentExpiry={null} onPick={(v) => setDraft((d) => ({ ...d, expiresOn: v }))} />

        {error && <div className="pn-admin-error">{error}</div>}

        <div className="pn-admin-form-actions">
          <button type="button" className="pn-btn pn-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="pn-btn pn-btn-accent" onClick={submit} disabled={saving}>
            <UserAddOutlined /> {saving ? 'Creating…' : 'Create & email password'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ============================ Edit access ============================ */

function EditUserModal({ user, onClose, onSaved }) {
  const [subscription, setSubscription] = useState('free');
  const [expiresOn, setExpiresOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setSubscription(user.subscription || 'free');
      setExpiresOn(toDateInput(user.subscription_expires_at));
      setError('');
    }
  }, [user]);

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await onSaved({
        userId: user.id,
        subscription,
        subscription_expires_at: fromDateInput(expiresOn),
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save the changes.');
    }
    setSaving(false);
  };

  return (
    <Modal title="Edit access" open={!!user} onCancel={onClose} destroyOnClose footer={null} width={520}>
      {user && (
        <div className="pn-admin-form">
          <div className="pn-admin-who">
            <div className="pn-admin-avatar">{(user.name || user.email || '?').charAt(0).toUpperCase()}</div>
            <div>
              <div className="n">{user.name}</div>
              <div className="e">{user.email}</div>
            </div>
          </div>

          <div className="pn-fields">
            <Field label="Plan">
              <select className="pn-control" value={subscription} onChange={(e) => setSubscription(e.target.value)}>
                {SUBSCRIPTION_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Access expires">
              <input className="pn-control" type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
            </Field>
          </div>

          <ExpiryPresets currentExpiry={user.subscription_expires_at} onPick={setExpiresOn} />

          {!expiresOn && (
            <div className="pn-admin-warn">
              With no expiry date this user is locked out of the app entirely.
            </div>
          )}
          {error && <div className="pn-admin-error">{error}</div>}

          <div className="pn-admin-form-actions">
            <button type="button" className="pn-btn pn-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="pn-btn pn-btn-accent" onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ============================ Page ============================ */

export function AdminPage() {
  const [adminState, setAdminState] = useState('checking'); // checking | allowed | denied
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchUsersFromServer();
      setUsers(Array.isArray(data) ? data : data?.users || []);
    } catch (error) {
      messageApi.error('Failed to load users');
      console.error(error);
    }
    setLoading(false);
  }, [messageApi]);

  // Ask the server whether this session is an admin before rendering anything.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await fetchAdminStatusFromServer();
        if (cancelled) return;
        if (status?.isAdmin) {
          setAdminState('allowed');
          loadUsers();
        } else {
          setAdminState('denied');
        }
      } catch (error) {
        if (!cancelled) setAdminState('denied');
      }
    })();
    return () => { cancelled = true; };
  }, [loadUsers]);

  const createUser = async (payload) => {
    const created = await addUserOnServer(payload);
    messageApi.success(`Account created — password emailed to ${payload.email}`);
    // Prefer the server's row (it owns id/created_at); fall back to a reload.
    if (created?.user?.id) setUsers((prev) => [created.user, ...prev]);
    else loadUsers();
  };

  const saveUser = async (payload) => {
    const saved = await updateUserOnServer(payload);
    messageApi.success('Access updated');
    setUsers((prev) => prev.map((u) => (
      u.id === payload.userId ? { ...u, ...(saved?.user || payload), id: u.id } : u
    )));
  };

  const confirmDelete = async () => {
    const user = deleting;
    try {
      await deleteUserOnServer(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      messageApi.success(`Deleted ${user.email}`);
    } catch (error) {
      messageApi.error('Failed to delete the user');
      console.error(error);
    }
    setDeleting(null);
  };

  const stats = useMemo(() => {
    const counts = { total: users.length, premium: 0, soon: 0, blocked: 0 };
    users.forEach((u) => {
      if (u.subscription === 'premium') counts.premium += 1;
      const { tone } = expiryStatus(u);
      if (tone === 'soon') counts.soon += 1;
      if (tone === 'expired' || tone === 'none') counts.blocked += 1;
    });
    return counts;
  }, [users]);

  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (tierFilter !== 'all' && u.subscription !== tierFilter) return false;
      if (!q) return true;
      return `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q);
    });
  }, [users, search, tierFilter]);

  if (adminState === 'checking') {
    return <div className="pn-admin-checking">Checking admin access…</div>;
  }
  if (adminState === 'denied') {
    return <Unauthorized />;
  }

  return (
    <>
      {contextHolder}
      <div className="pn-admin">
        <PageHead eyebrow="Admin" title="Users & access"
          sub={<>Create accounts and control who can use PreNumbers. Access ends on a user's <b>expiry date</b> — a user with no date set is locked out.</>}>
          <button type="button" className="pn-btn pn-btn-ghost" onClick={loadUsers} disabled={loading}>
            <ReloadOutlined /> Refresh
          </button>
          <button type="button" className="pn-btn pn-btn-accent" onClick={() => setAddOpen(true)}>
            <UserAddOutlined /> Add account
          </button>
        </PageHead>

        <div className="pn-admin-stats">
          <StatTile icon={<TeamOutlined />} k="Total users" v={stats.total} feature />
          <StatTile icon={<CrownOutlined />} k="Premium" v={stats.premium} />
          <StatTile icon={<ClockCircleOutlined />} k="Expiring in 7d" v={stats.soon} />
          <StatTile icon={<StopOutlined />} k="No access" v={stats.blocked} />
        </div>

        <div className="pn-admin-toolbar">
          <div className="pn-admin-search">
            <SearchOutlined className="ic" />
            <input className="pn-control" placeholder="Search by name or email"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="pn-control pn-admin-filter" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
            <option value="all">All plans</option>
            {SUBSCRIPTION_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="pn-admin-empty"><div className="t">Loading users…</div></div>
        ) : visibleUsers.length === 0 ? (
          <div className="pn-admin-empty">
            <div className="ic"><TeamOutlined /></div>
            <div className="t">{users.length === 0 ? 'No users yet' : 'No users match that search'}</div>
            <div>{users.length === 0 ? 'Create the first account to get started.' : 'Try a different name, email, or plan.'}</div>
          </div>
        ) : (
          <div className="pn-table-wrap pn-admin-table">
            <table className="pn-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Plan</th>
                  <th>Access expires</th>
                  <th>Joined</th>
                  <th className="ta-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map((user) => {
                  const status = expiryStatus(user);
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="pn-admin-who">
                          <div className="pn-admin-avatar">{(user.name || user.email || '?').charAt(0).toUpperCase()}</div>
                          <div>
                            <div className="tname">{user.name || 'Unnamed user'}</div>
                            <div className="tdesc">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={'pn-badge ' + (user.subscription === 'premium' ? 'sales' : 'sub')}>
                          {user.subscription === 'premium' && <CrownOutlined />} {user.subscription || 'free'}
                        </span>
                      </td>
                      <td>
                        <div className="pn-admin-expiry" title={status.hint}>
                          <span className={'pn-admin-dot ' + status.tone} />
                          <div>
                            <div className="v">{formatDate(user.subscription_expires_at)}</div>
                            <div className={'s ' + status.tone}>{status.label}</div>
                          </div>
                        </div>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <div className="pn-admin-actions">
                          <button type="button" className="pn-btn pn-btn-ghost pn-btn-sm" onClick={() => setEditing(user)}>
                            <EditOutlined /> Edit
                          </button>
                          <button type="button" className="pn-btn pn-btn-danger pn-btn-sm" onClick={() => setDeleting(user)}>
                            <DeleteOutlined />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddUserModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={createUser} />
      <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={saveUser} />

      <Modal title="Delete user" open={!!deleting} onCancel={() => setDeleting(null)} onOk={confirmDelete}
        okText="Delete user" okButtonProps={{ danger: true }} width={460}>
        {deleting && (
          <p className="pn-admin-confirm">
            Permanently delete <b>{deleting.name}</b> ({deleting.email})? This also removes every company,
            entity and dashboard they own. This cannot be undone.
          </p>
        )}
      </Modal>
    </>
  );
}

export default AdminPage;
